import { call, put, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  ContractorPortalSendCode,
  ContractorPortalSetValidateLockedUntil,
  ContractorPortalUpdateEmail,
  ContractorPortalUpdateEmailSendCode,
  ContractorPortalUpdatePhone,
  ContractorPortalUpdatePhoneSendCode,
  ContractorPortalVerifyCode,
} from 'src/constants/actionTypes'
import { ApiService, callContractorPortalSecureApi } from '../api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import { captureSentryError } from 'src/utils/sentry'
import { getApiErrorMessage } from 'src/utils/errorHandlers'
import { EnterCodeResponseError, SignInPhoneResponse } from 'src/interfaces/auth'

interface ContractorPortalSendCodeRequest {
  resend?: boolean
}

function* onContractorPortalSendCode(action: AsyncAction<ContractorPortalSendCodeRequest>) {
  try {
    const body: ContractorPortalSendCodeRequest = {}

    if (action?.payload?.resend) {
      body.resend = true
    }

    const { payload }: { payload: SignInPhoneResponse } = yield call(
      callContractorPortalSecureApi,
      Endpoints.ContractorPortalSendCode,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body,
      },
    )

    yield put(ContractorPortalSendCode.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalSendCode] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to send code')

    yield put(ContractorPortalSendCode.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorPortalVerifyCodeRequest {
  code: string
}

function* onContractorPortalVerifyCode(action: AsyncAction<ContractorPortalVerifyCodeRequest>) {
  try {
    const { code } = action.payload

    const { payload }: { payload: { token: string } } = yield call(
      callContractorPortalSecureApi,
      Endpoints.ContractorPortalVerifyCode,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body: { code },
      },
    )

    yield put(ContractorPortalVerifyCode.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalVerifyCode] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to verify code')
    const error = err.payload as EnterCodeResponseError

    if (error.validateLockedUntil) {
      yield put(ContractorPortalSetValidateLockedUntil.success(error.validateLockedUntil))

      if (action.next) {
        action.next(null)
      }

      return
    }

    yield put(ContractorPortalVerifyCode.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorPortalUpdatePhoneSendCodeRequest {
  phone: string
  token: string
  resend?: boolean
}

function* onContractorPortalUpdatePhoneSendCode(
  action: AsyncAction<ContractorPortalUpdatePhoneSendCodeRequest>,
) {
  try {
    const { phone, token, resend } = action.payload

    const body: ContractorPortalUpdatePhoneSendCodeRequest = { phone, token }

    if (resend) {
      body.resend = true
    }

    const { payload }: { payload: SignInPhoneResponse } = yield call(
      callContractorPortalSecureApi,
      Endpoints.ContractorPortalUpdatePhoneSendCode,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        query: { token },
        body,
      },
    )

    yield put(ContractorPortalUpdatePhoneSendCode.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalUpdatePhoneSendCode] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to send code')

    yield put(ContractorPortalUpdatePhoneSendCode.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorPortalUpdatePhoneRequest {
  token: string
  code: string
  phone: string
}

function* onContractorPortalUpdatePhone(action: AsyncAction<ContractorPortalUpdatePhoneRequest>) {
  try {
    const { token, code, phone } = action.payload

    yield call(callContractorPortalSecureApi, Endpoints.ContractorPortalUpdatePhone, {
      apiService: ApiService.Paraworks,
      method: 'PUT',
      query: { token },
      body: { code, phone },
    })

    yield put(ContractorPortalUpdatePhone.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalUpdatePhone] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to update phone number')

    yield put(ContractorPortalUpdatePhone.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorPortalUpdateEmailSendCodeRequest {
  email: string
  token: string
  resend?: boolean
}

function* onContractorPortalUpdateEmailSendCode(
  action: AsyncAction<ContractorPortalUpdateEmailSendCodeRequest>,
) {
  try {
    const { email, token, resend } = action.payload

    const body: ContractorPortalUpdateEmailSendCodeRequest = { email, token }

    if (resend) {
      body.resend = true
    }

    const { payload }: { payload: SignInPhoneResponse } = yield call(
      callContractorPortalSecureApi,
      Endpoints.ContractorPortalUpdateEmailSendCode,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        query: { token },
        body,
      },
    )

    yield put(ContractorPortalUpdateEmailSendCode.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalUpdateEmailSendCode] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to send code')

    yield put(ContractorPortalUpdateEmailSendCode.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorPortalUpdateEmailRequest {
  token: string
  code: string
  email: string
}

function* onContractorPortalUpdateEmail(action: AsyncAction<ContractorPortalUpdateEmailRequest>) {
  try {
    const { token, code, email } = action.payload

    const { payload }: { payload: any } = yield call(
      callContractorPortalSecureApi,
      Endpoints.ContractorPortalUpdateEmail,
      {
        apiService: ApiService.Paraworks,
        method: 'PUT',
        query: { token },
        body: { code, email },
      },
    )

    yield put(ContractorPortalUpdateEmail.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalUpdateEmail] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to update email address')

    yield put(ContractorPortalUpdateEmail.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* contractorPortalDriverDataSaga() {
  yield takeEvery(ContractorPortalSendCode.type.REQUEST, onContractorPortalSendCode)
  yield takeEvery(ContractorPortalVerifyCode.type.REQUEST, onContractorPortalVerifyCode)
  yield takeEvery(
    ContractorPortalUpdateEmailSendCode.type.REQUEST,
    onContractorPortalUpdateEmailSendCode,
  )
  yield takeEvery(ContractorPortalUpdateEmail.type.REQUEST, onContractorPortalUpdateEmail)
  yield takeEvery(
    ContractorPortalUpdatePhoneSendCode.type.REQUEST,
    onContractorPortalUpdatePhoneSendCode,
  )
  yield takeEvery(ContractorPortalUpdatePhone.type.REQUEST, onContractorPortalUpdatePhone)
}
